// React-related imports
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

// Third-party library imports
import * as dates from "@simplybenefits/sb-dates";
import {
    Services,
    Button,
    Text,
    BubbleSelectBlock, 
    i18n,
    tokens,
    EftDetailsForm
} from '@simplybenefits/sb-ui-library';
import FormControl from "@mui/material/FormControl";

// Project-specific imports
import { initStripe } from '../../../actions/signup.js';

// Image imports
import englishCheque from '../../../assets/void-cheque.png';
import frenchCheque from '../../../assets/french-cheque.jpg';

// actions
import { validateBankingDetails } from '../../../actions/user.js';

/**
 * @param bankingDetails
 * @param isMarketplaceOnly
 * @returns {{default, paymentsOnly, useSameBankAccount: boolean}|{default: {institutionNumError: boolean, institutionNum: string, accountNum: string, accountHolderError: boolean, transitNum: string, accountNumError: boolean, branchValidationError: boolean, accepted: boolean, transitNumError: boolean, accountHolder: string, isValidated: boolean, acceptedError: boolean, institutionName: string, institutionAddress: string, timestampOfAcceptance: null}, paymentsOnly: {institutionNumError: boolean, institutionNum: string, accountNum: string, accountHolderError: boolean, transitNum: string, accountNumError: boolean, branchValidationError: boolean, accepted: boolean, transitNumError: boolean, accountHolder: string, isValidated: boolean, acceptedError: boolean, institutionName: string, institutionAddress: string, timestampOfAcceptance: null}, useSameBankAccount: (boolean|null)}}
 */
const initialEftDetailsState = (bankingDetails, isMarketplaceOnly = false) => {
    if (!bankingDetails) {
        return {
            useSameBankAccount: isMarketplaceOnly ? true : null,
            default: {
                accountHolder: '',
                institutionName: '',
                institutionAddress: '',
                institutionNum: '',
                transitNum: '',
                accountNum: '',
                isValidated: false,
                accepted: false,
                timestampOfAcceptance: null,
                accountHolderError: false,
                institutionNumError: false,
                transitNumError: false,
                accountNumError: false,
                acceptedError: false,
                branchValidationError: false
            },
            paymentsOnly: {
                accountHolder: '',
                institutionName: '',
                institutionAddress: '',
                institutionNum: '',
                transitNum: '',
                isValidated: false,
                accountNum: '',
                accepted: false,
                timestampOfAcceptance: null,
                accountHolderError: false,
                institutionNumError: false,
                transitNumError: false,
                accountNumError: false,
                acceptedError: false,
                branchValidationError: false
            }
        };
    }

    return {
        useSameBankAccount: !bankingDetails.paymentsOnly?.institutionName,
        default: {
            ...bankingDetails.default
        },
        paymentsOnly: {
            ...bankingDetails.paymentsOnly
        }
    };
};

/**
 * @param userID
 * @param accountID
 * @param company
 * @param isStripeSuccess
 * @param bankingDetails
 * @param stepCompletion
 * @param setSavedState
 * @param data
 * @param whitelabel
 * @param dispatch
 * @param bankingDetailsVerifying
 * @param isMarketplaceOnly
 * @returns {JSX.Element}
 * @constructor
 */
const BankingDetails = ({ userID, accountID, company, isStripeSuccess, bankingDetails, stepCompletion, setSavedState, data, whitelabel, dispatch, bankingDetailsVerifying, isMarketplaceOnly }) => {
    const [ paymentSelection, setPaymentSelection ] = useState(isStripeSuccess ? 'CC' : 'EFT');
    const [ eftDetails, setEftDetails ] = useState(initialEftDetailsState(bankingDetails, isMarketplaceOnly));

    const styles = {
        form: {
            display: 'flex',
            flexDirection: 'column'
        },
        column: {
            display: 'flex',
            flexDirection: 'column'
        },
        row: {
            display: 'flex',
            flexDirection: 'row'
        },
        divider: {
            width: 40
        },
        visibilityIcon: {
            position: 'absolute',
            marginLeft: -30
        },
        stepperBtnContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '20px'
        },
        stepperNextBtn: {
            float: 'right',
            marginRight: '5px !important'
        }
    };


    /*
     * This useEffect handles the case where a user validates the default form, then navigates away, 
     * tries to change to dual bank accounts, and then tries to validate their details. In the useEffect, we are
     * making sure that the paymentsOnly form is populated before the user tries to validate the form 
     */
    useEffect(() => {
        if (!eftDetails.useSameBankAccount) {
            setEftDetails((prevEftDetails) => ({
                ...prevEftDetails,
                default: {
                    ...prevEftDetails.default
                },
                paymentsOnly: {
                    accountHolder: '',
                    institutionName: '',
                    institutionAddress: '',
                    institutionNum: '',
                    transitNum: '',
                    isValidated: false,
                    accountNum: '',
                    accepted: false,
                    timestampOfAcceptance: null,
                    accountHolderError: false,
                    institutionNumError: false,
                    transitNumError: false,
                    accountNumError: false,
                    acceptedError: false,
                    branchValidationError: false
                }
            }));
        }
    }, [ eftDetails.useSameBankAccount ]);
  
    /**
     * Handles changes in the input values for a specified form type in banking details.
     *
     * @function
     * @private
     * @param {string} formType - The type of form (e.g., "default", "paymentsOnly").
     * @param {string} type - The specific property to update within the form type.
     * @param {*} value - The new value to set for the specified property.
     * @param {Event} event - The event triggering the change.
     */
    const _handleChange = (formType, type, value, event) => {
        setSavedState('bankingDetails', false);
        if (event && event.stopPropagation) {
            event.stopPropagation();
        }
        
        setEftDetails((prevEftDetails) => {
            const updatedFormValue = {
                ...prevEftDetails[formType],
                [type]: value
            };
        
            if (type === 'accepted' && value === true) {
                updatedFormValue.timestampOfAcceptance = dates.getUTC();
            }
        
            return {
                ...prevEftDetails,
                [formType]: updatedFormValue
            };
        });
    };
     

    /**
     * Updates the state of the component with changes specific to a form type in the banking details.
     *
     * @param {string} formType - The type of form (e.g., "default", "paymentsOnly").
     * @param {Object} newState - The new state changes for the specified form type.
     * @param {string} newState.property - The specific property to update within the form type.
     * @param {*} newState.value - The new value to set for the specified property.
     * 
     */
    const _handleStateChanges = (formType, newState) => {
        setEftDetails((prevEftDetails) => ({
            ...prevEftDetails,
            [formType]: {
                ...prevEftDetails[formType],
                ...newState
            }
        }));
    };

    /**
     * Validates the specified form type in the banking details by checking input fields against predefined rules.
     *
     * @async
     * @function
     * @param {string} formType - The type of form to validate (e.g., "default", "paymentsOnly").
     * @returns {boolean} Returns true if the specified form is successfully validated, false otherwise.
     * 
     */
    const validateForm = async (formType) => {
        const form = eftDetails[formType];
        const accountHolderError = await Services.validateInput('bankAccountHolderName', form.accountHolder);
        const institutionNumError = await Services.validateInput('institutionNum', form.institutionNum);
        const transitNumError = await Services.validateInput('transitNum', form.transitNum);
        const accountNumError = await Services.validateInput('checkEmpty', form.accountNum);
        const acceptedError = form.accepted ? null : i18n.t(tokens.labels.banking.youMustAcceptTermsError);

        _handleStateChanges(formType, {
            accountHolderError: accountHolderError,
            institutionNumError: institutionNumError,
            transitNumError: transitNumError,
            accountNumError: accountNumError,
            acceptedError
        });
  
        return !accountHolderError && !institutionNumError && !transitNumError && !accountNumError && !acceptedError;
    };
 
    /**
     * Validates the banking details forms based on the current configuration.
     *
     * @async
     * @function
     * @private
     * @returns {Promise<boolean>} Resolves with true if the forms are successfully validated, false otherwise.
     */
    const _validateForms = async () => {
        if (eftDetails.useSameBankAccount) {
            return validateForm('default');
        }
  
        const isDefaultFormValid = await validateForm('default');
        const isPaymentsOnlyFormValid = await validateForm('paymentsOnly');
  
        return isDefaultFormValid && isPaymentsOnlyFormValid;
    };

    /**
     * Handles fetching and validating branch details for the provided institution and transit numbers.
     *
     * @async
     * @returns {boolean} Returns true if branch details are successfully validated, false otherwise.
     * @private
     */
    const validateBranchDetails = async (formType) => {
        const branchDetails = await dispatch(validateBankingDetails(eftDetails[formType].institutionNum, eftDetails[formType].transitNum));

        if (branchDetails.data === null && branchDetails.isFound === false) {
            _handleChange(formType, 'branchValidationError', true);
            return false;
        }
      
        const {
            frenchInstitutionName,
            englishInstitutionName,
            branchDomicile,
            civicAddress,
            postalAddress,
            townCity,
            provinceCode,
            postalCode
        } = branchDetails.data;
      
        const validatedInstitutionName = i18n.locale === 'fr' ? frenchInstitutionName : englishInstitutionName;
        const validatedInstitutionAddress = [ branchDomicile, civicAddress || postalAddress, townCity, provinceCode, postalCode ].filter(Boolean).join(', ');
      
        _handleStateChanges(formType, {
            institutionName: validatedInstitutionName,
            institutionAddress: validatedInstitutionAddress,
            institutionNumError: null,
            transitNumError: null,
            branchValidationError: false,
            isValidated: true
        });
      
        return true;
    };
     
    /**
     * Validates banking details by checking form validations and branch details for different form types.
     *
     * @async
     * @private
     * @function
     * @returns {boolean} Returns true if banking details are successfully validated, false otherwise.
     * 
     */
    const _validateBankingDetails = async () => {
        const formsValidated = await _validateForms();

        if (!formsValidated) {
            return false;
        }
      
        if (eftDetails.useSameBankAccount) {
            return validateBranchDetails('default');
        }
      
        const isDefaultBranchValid = await validateBranchDetails('default');
        const isPaymentsOnlyBranchValid = await validateBranchDetails('paymentsOnly');
      
        return isDefaultBranchValid && isPaymentsOnlyBranchValid;
    };

    /**
     * Saves banking details and continues to the next step.
     *
     * @async
     * @private
     * @function
     * @param {boolean} [skipCompletion=false] - Flag indicating whether to skip setting completion state.
     * @returns {boolean} Indicates the success of the operation.
     */
    const _saveBankingDetailsAndContinue = (skipCompletion = false) => {
        const validatedBankingDetails = {
            paymentSelection: paymentSelection || 'EFT',
            default: {
                accountHolder: eftDetails.default.accountHolder,
                institutionName: eftDetails.default.institutionName,
                institutionAddress: eftDetails.default.institutionAddress,
                institutionNum: `${ eftDetails.default.institutionNum }`,
                transitNum: `${ eftDetails.default.transitNum }`,
                accountNum: `${ eftDetails.default.accountNum }`,
                accepted: eftDetails.default.accepted,
                timestampOfAcceptance: Number(eftDetails.default.timestampOfAcceptance)
            }
        };

        if (!eftDetails.useSameBankAccount) {
            validatedBankingDetails.paymentsOnly = {
                accountHolder: eftDetails.paymentsOnly.accountHolder,
                institutionName: eftDetails.paymentsOnly.institutionName,
                institutionAddress: eftDetails.paymentsOnly.institutionAddress,
                institutionNum: `${ eftDetails.paymentsOnly.institutionNum }`,
                transitNum: `${ eftDetails.paymentsOnly.transitNum }`,
                accountNum: `${ eftDetails.paymentsOnly.accountNum }`,
                accepted: eftDetails.paymentsOnly.accepted,
                timestampOfAcceptance: Number(eftDetails.paymentsOnly.timestampOfAcceptance)
            };
        }

        if (!skipCompletion) {
            // make the saved state to true, we have hit next and everything is valid
            setSavedState('bankingDetails', true);
        }

        stepCompletion({ ...validatedBankingDetails }, 'bankingDetails', false);
        return false;
    };


    /**
     * Initializes the Stripe integration for credit card payments.
     * 
     * Sets the payment selection to 'CC', saves banking details, and continues.
     * Redirects to Stripe to set up credit card information.
     * 
     * @async
     * @function
     * @returns {Promise<void>} Resolves once the Stripe integration is initialized.
     */
    const _initStripeIntegration = async () => {
        setPaymentSelection("CC");

        await _saveBankingDetailsAndContinue(); // make sure we save the banking details etc.. the page is going to refresh so we don't want to lose any progress

        // if we have already set the CC info in stripe we don't need to go back to Stripe
        if (isStripeSuccess && data.paymentType === 'CC' && data.stripeCustomerID !== null) {
            await setSavedState('bankingDetails', true);
            return;
        }

        window.location.href = await dispatch(initStripe(userID, accountID, i18n.locale));
    };

    /**
     * Handles the onSave action for the banking details form:
     * - If using the same bank account and default details are not validated, triggers banking details validation.
     * - If not using the same account and either default or payments-only details are not validated, triggers banking details validation.
     * - Otherwise, saves the banking details and continues to the next step.
     *
     * @async
     * @function
     * @returns {Promise<void|null>} Resolves with null if validation is triggered, otherwise resolves after saving and continuing.
     * @throws {Error} Throws an error if any translation token is missing or if an unexpected condition occurs.
     * 
     */
    const onSave = async () => {
        const isSameBankAccount = eftDetails.useSameBankAccount;
        const isDefaultValidated = eftDetails.default.isValidated;
        const isPaymentsOnlyValidated = eftDetails.paymentsOnly.isValidated;

        if (isSameBankAccount && !isDefaultValidated) {
            return await _validateBankingDetails();
        } else if (!isSameBankAccount && (!isDefaultValidated || !isPaymentsOnlyValidated)) {
            return await _validateBankingDetails();
        }

        return await _saveBankingDetailsAndContinue();
    };

    /**
     * Generates the save button text based on the current state:
     * - If banking details are being verified, displays "Validating."
     * - If using the same bank account and default details are validated, shows "Save Banking Details."
     * - If not using the same account and both default and payments-only details are validated, shows "Save Banking Details."
     * - Otherwise, displays "Validate Banking Details."
     *
     * @function
     * @returns {string} The text for the save button.
     * 
     */
    const saveButtonText = bankingDetailsVerifying
        ? i18n.t(tokens.labels.banking.validating)
        : eftDetails.useSameBankAccount
            ? eftDetails.default.isValidated
                ? i18n.t(tokens.buttons.save).toUpperCase()
                : i18n.t(tokens.labels.banking.validateBanking).toUpperCase()
            : eftDetails.default.isValidated && eftDetails.paymentsOnly.isValidated
                ? i18n.t(tokens.buttons.save).toUpperCase()
                : i18n.t(tokens.labels.banking.validateBanking).toUpperCase();

    /**
     * Generates the EFT form containing banking details and user selections.
     *
     * @function
     * @private
     * @returns {JSX.Element} The JSX element representing the EFT form.
     */
    const eftForm = () => {
        const chequeImage = i18n.locale === "fr" ? frenchCheque : englishCheque;
        
        return (
            <div style={styles.form}>
                <Text
                    small
                    margin="10px 0"
                >
                    { i18n.t(tokens.labels.banking.bankingDetailsRequest) }
                </Text>

                <Text
                    small
                    margin="10px 0 20px 0"
                >
                    <span style={{ fontWeight: 'bold', fontSize: 14 }}>{ i18n.t(tokens.labels.banking.example) }: </span>{ i18n.t(tokens.labels.banking.chequeAndPersonalBankingWebsiteInformation) }
                </Text>

                <img
                    alt="Example Cheque"
                    src={ chequeImage }
                    style={{ width: '100%', height: '100%' }}
                />

                {
                    !isMarketplaceOnly &&
                        <FormControl component="fieldset">
                            <Text
                                small
                                margin="30px 0 0 0"
                            >
                                { i18n.t(tokens.labels.banking.bankAccountForReimbursementsAndPremiumPayments) }
                            </Text>

                            <BubbleSelectBlock
                                first={{
                                    title: i18n.t(tokens.buttons.yes),
                                    selected: eftDetails.useSameBankAccount === true,
                                    field: 'useSameBankAccountYes'
                                }}
                                second={{
                                    title: i18n.t(tokens.buttons.no),
                                    selected: eftDetails.useSameBankAccount === false,
                                    field: 'useSameBankAccountNo'
                                }}
                                handleChange={ (field) => {
                                    setEftDetails((prevEftDetails) => ({
                                        ...prevEftDetails,
                                        useSameBankAccount: field === "useSameBankAccountNo" ? false : !eftDetails.useSameBankAccount
                                    }));
                                }}
                            />
                        </FormControl>
                }

                {
                    eftDetails.useSameBankAccount !== null ?
                        <>
                            {
                                eftDetails.useSameBankAccount ?
                                    <EftDetailsForm
                                        formType={ "default" }
                                        eftDetails={ eftDetails }
                                        handleStateChanges={ _handleStateChanges }
                                        handleChange={ _handleChange }
                                        whitelabel={ whitelabel }
                                        isMarketplaceOnly={ isMarketplaceOnly }
                                    />
                                    :
                                    <>
                                        <EftDetailsForm
                                            formType={ "default" }
                                            eftDetails={ eftDetails }
                                            handleStateChanges={ _handleStateChanges }
                                            handleChange={ _handleChange }
                                            whitelabel={ whitelabel }
                                            isMarketplaceOnly={ isMarketplaceOnly }
                                        />
                                        <EftDetailsForm
                                            formType={ 'paymentsOnly' }
                                            eftDetails={ eftDetails }
                                            handleStateChanges={ _handleStateChanges }
                                            handleChange={ _handleChange }
                                            whitelabel={ whitelabel }
                                            isMarketplaceOnly={ isMarketplaceOnly }
                                        />
                                    </>
                            }
                        </>
                        :
                        null
                }

                <div style={styles.stepperBtnContainer}>
                    <Button
                        style={ styles.stepperNextBtn }
                        onClick={ onSave }
                        color="primary"
                    >
                        { saveButtonText }
                    </Button>
                </div>
            </div>
        );
    };

    // hard coded account id's for groups that have member pay where members can pay by Credit Card
    if (accountID === 'Ob8A3g' || accountID === 'mQoVNg' || accountID === 'zpvj2p' || accountID === 'GpwwZp' || accountID === '0Qj7Gp' || accountID === '3gZl1p') {
        return (
            <div>
                <Text largest bold margin="0 0 10px 0">{ i18n.t(tokens.labels.banking.paymentSelection) }</Text>

                <div style={styles.form}>
                    <Text
                        small
                        margin="10px 0"
                    >
                        { i18n.t(tokens.labels.banking.youHaveBeenGivenA) }{" "}
                        <span style={{ fontWeight: 'bold', fontSize: 14 }}>
                            { i18n.t(tokens.labels.banking.payByMember) }{" "}</span> 
                        { i18n.t(tokens.labels.banking.benefitsAccountResponsibilityAndPaymentSelection) } 
                        <span style={{ fontWeight: 'bold', fontSize: 14 }}>{" "}{ i18n.t(tokens.labels.banking.creditCardFeeNote) }</span>
                    </Text>

                    <BubbleSelectBlock
                        first={{
                            title: i18n.t(tokens.labels.banking.directDebit),
                            selected: paymentSelection === 'EFT',
                            field: 'paymentSelectionEFT'
                        }}
                        second={{
                            title: i18n.t(tokens.labels.banking.creditCard),
                            selected: paymentSelection === 'CC',
                            field: 'paymentSelectionCC'
                        }}
                        handleChange={ (field) => {
                            if (field === 'paymentSelectionCC') {
                                setPaymentSelection('CC');
                            } else {
                                setPaymentSelection('EFT');
                            }
                        }}
                    />
                </div>

                {
                    paymentSelection === 'CC' &&
                            <div style={styles.form}>
                                <Text
                                    small
                                    margin="10px 0"
                                >
                                    <Button
                                        style={ styles.stepperNextBtn }
                                        onClick={ _initStripeIntegration }
                                        color="primary"
                                        fullWidth
                                    >
                                        { i18n.t(tokens.buttons.continue) }
                                    </Button>
                                </Text>
                            </div>
                }

                {
                    paymentSelection === 'EFT' &&
                       eftForm()
                }

            </div>
        );
    }

    return (
        <React.Fragment>
            <Text largest bold margin="0 0 10px 0">{ i18n.t(tokens.labels.banking.paymentSelection) }</Text>

            <div style={styles.form}>
                <Text
                    small
                    margin="10px 0"
                >
                    { i18n.t(tokens.labels.banking.youHaveBeenGivenA) } <span style={{ fontWeight: 'bold', fontSize: 14 }}>{ i18n.t(tokens.labels.banking.payByMember) }</span> { i18n.t(tokens.labels.banking.benefitsAccountResponsibility) }
                </Text>
            </div>

            { eftForm() }
        </React.Fragment>
    );
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    status: state,
    loading: state.user.loading,
    bankingDetailsVerifying: state.user.bankingDetailsVerifying,
    whitelabel: state.whitelabel
});

export default connect(mapStateToProps)(BankingDetails);